import * as React from 'react';
import { ReactNode, useEffect, useState, useMemo } from 'react';

import { Expand } from 'iconoir-react';
import { lighten } from 'polished';
import styled, { ThemeProvider } from 'styled-components';

import { SPACING, COLOR, THEME } from '../../packages/constants';
import { Button } from '../button';
import { CheckboxInput } from '../checkbox-input';
import { ThumbnailVariants } from './preview';

export interface PreviewNavigationButtonsProps {
    showFullscreenButton: boolean;
    showNavigation: boolean;
    isSinglePage: boolean;
    showThumbnails: boolean;
    isFullscreen: boolean;
    onToggleThumbnails: () => void;
    onToggleNavigation: () => void;
    onToggleFullscreen: () => void;
    customNavigationBarContent?: ReactNode;
    reportTitle?: string;
    isSustain?: boolean;
    thumbnailVariant: ThumbnailVariants;
}

type ButtonsToShow = 'none' | 'thumbnail_only' | 'fullscreen_only' | 'nav_and_fullscreen' | 'all';

interface StyledSettingsProps {
    fullscreenAvailable: boolean;
    isSustain?: boolean;
}

const StyledSettings = styled.div<StyledSettingsProps>`
    display: flex;
    justify-content: ${(props) => (props.isSustain ? 'flex-start' : 'space-between')};
    align-items: ${(props) => (props.isSustain ? 'flex-start' : 'center')};
    padding: ${(props) => (props.fullscreenAvailable ? SPACING.MEDIUM : `0 0 ${SPACING.MEDIUM} ${SPACING.XX_SMALL}`)};
    border-bottom: 1px solid
        ${(props) => (props.fullscreenAvailable ? lighten(0.3, COLOR.MAIN.FORM_FIELD_DARK) : 'transparent')};
    background-color: ${(props) => (props.fullscreenAvailable ? COLOR.PRIMARY.WHITE : 'transparent')};
`;

const CheckboxInputContainer = styled.div`
    display: flex;
    column-gap: ${SPACING.MEDIUM};
    min-width: 300px;
`;

const FullScreenButton = styled.div`
    align-self: flex-end;
    flex-shrink: 0;
`;

const ReportTitle = styled.div`
    flex-grow: 1;
    text-align: center;
    line-height: 1.5;
    font-size: 18px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-left: 30px;
    padding-right: 30px;
`;

interface StyledCheckbox {
    isSustain?: boolean;
}

const StyledCheckbox = styled(CheckboxInput)<StyledCheckbox>`
    flex-shrink: 0;
    min-width: ${(props) => (props.isSustain ? '250px' : '')};
`;

const StyledButton = styled(Button)`
    line-height: normal;
    border-radius: 5px;
    border-width: 0;
    font-weight: 600;
    column-gap: 5px;
    font-size: 13px;
    padding-bottom: 7px;
    padding-top: 8px;
    text-decoration: underline;
    text-underline-position: under;
    color: #031c44;
    background-color: #fff;

    &:hover:not(:disabled) {
        background-color: #f3f4f7;
        color: #031c44;
    }
`;

export const PreviewNavigationButtons: React.FC<React.PropsWithChildren<PreviewNavigationButtonsProps>> = ({
    showFullscreenButton,
    showNavigation,
    isSinglePage,
    showThumbnails,
    isFullscreen,
    onToggleThumbnails,
    onToggleNavigation,
    onToggleFullscreen,
    customNavigationBarContent,
    reportTitle,
    isSustain,
    thumbnailVariant,
}) => {
    const [buttonsToShow, setButtonsToShow] = useState<ButtonsToShow>('none');

    useEffect(() => {
        if (isFullscreen) setButtonsToShow('none');
        else if (isSinglePage && showFullscreenButton) setButtonsToShow('fullscreen_only');
        else if (showFullscreenButton && thumbnailVariant !== 'hide') setButtonsToShow('all');
        else if (showFullscreenButton) setButtonsToShow('nav_and_fullscreen');
        else if (!isSinglePage && thumbnailVariant !== 'hide') setButtonsToShow('thumbnail_only');
        else setButtonsToShow('none');
    }, [showFullscreenButton, isSinglePage, isFullscreen]);

    const fullscreenButtonVisible = useMemo(() => {
        return buttonsToShow === 'all' || buttonsToShow === 'fullscreen_only' || buttonsToShow === 'nav_and_fullscreen';
    }, [buttonsToShow]);

    return (
        <>
            {(buttonsToShow !== 'none' || customNavigationBarContent) && (
                <StyledSettings
                    fullscreenAvailable={showFullscreenButton}
                    isSustain={!fullscreenButtonVisible && isSustain}
                >
                    {buttonsToShow === 'thumbnail_only' && (
                        <StyledCheckbox
                            id={'toggle-thumbnails'}
                            dataId={'preview__toggle-thumbnails'}
                            name={'toggle-thumbnails'}
                            label={'Show thumbnails'}
                            inline
                            onChange={onToggleThumbnails}
                            checked={showThumbnails}
                            isSustain={!fullscreenButtonVisible && isSustain}
                        />
                    )}
                    {buttonsToShow === 'nav_and_fullscreen' && (
                        <StyledCheckbox
                            id={'toggle-navigation'}
                            dataId={'preview__toggle-navigation'}
                            name={'toggle-navigation'}
                            label={'Show navigation'}
                            inline
                            onChange={onToggleNavigation}
                            checked={showNavigation}
                        />
                    )}
                    {buttonsToShow === 'all' && (
                        <CheckboxInputContainer>
                            <StyledCheckbox
                                id={'toggle-navigation'}
                                dataId={'preview__toggle-navigation'}
                                name={'toggle-navigation'}
                                label={'Show navigation'}
                                inline
                                onChange={onToggleNavigation}
                                checked={showNavigation}
                            />
                            <StyledCheckbox
                                id={'toggle-thumbnails'}
                                dataId={'preview__toggle-thumbnails'}
                                name={'toggle-thumbnails'}
                                label={'Show thumbnails'}
                                inline
                                onChange={onToggleThumbnails}
                                checked={showThumbnails}
                            />
                        </CheckboxInputContainer>
                    )}
                    {customNavigationBarContent && !isFullscreen && !fullscreenButtonVisible && isSustain && (
                        <>{customNavigationBarContent} </>
                    )}
                    {fullscreenButtonVisible && isSustain && <ReportTitle>{reportTitle}</ReportTitle>}
                    {fullscreenButtonVisible && !isSustain && (
                        <FullScreenButton>
                            <ThemeProvider theme={THEME.REPORTING}>
                                <Button dataId="preview__toggle-fullscreen" onClick={onToggleFullscreen}>
                                    Full-screen Mode
                                </Button>
                            </ThemeProvider>
                        </FullScreenButton>
                    )}
                    {fullscreenButtonVisible && isSustain && (
                        <FullScreenButton>
                            <StyledButton
                                size={'medium'}
                                onClick={onToggleFullscreen}
                                variant={'medium'}
                                dataId="full-screen-mode_btn"
                            >
                                <Expand strokeWidth={1.5} width={'16px'} height={'16px'} />
                                Full screen mode
                            </StyledButton>
                        </FullScreenButton>
                    )}
                </StyledSettings>
            )}
        </>
    );
};
